/**
 * Scheda giocatore (Andrea 2026-10-15, versione compatta):
 * - un blocco in alto con anagrafica a sinistra, possesso + valore TM al
 *   centro e le ultime 5 rilevazioni TM a destra;
 * - sotto, le statistiche della stagione in corso della lega (voti finali):
 *   presenze, medie, gol, assist, cartellini, rigori sbagliati, autogol e,
 *   solo per i portieri, gol subiti, rigori parati e porte inviolate.
 * Si apre dal catalogo, da Gestione Rosa e da Rose.
 */

import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { useSquadre } from "../../hooks/useFase7";
import { MobileShell } from "../shell/MobileShell";
import { PlayerAvatar } from "../common/PlayerAvatar";
import { FULL_BY_GROUP } from "../common/ruoliColors";
import { ruoloMantraClass } from "../common/ruoliMantra";
import { siglaRuolo } from "../common/nomenclatura";
import { localeCorrente } from "../../i18n";
import { tr } from "../../i18n";

interface ValoreTM {
  valore_milioni: string;
  data_rilevazione: string;
  fonte: string;
}

interface StatisticheStagione {
  stagione_anno: string;
  presenze: number;
  voto_medio: string | null;
  fantavoto_medio: string | null;
  gol: number;
  assist: number;
  ammonizioni: number;
  espulsioni: number;
  rigori_sbagliati: number;
  autogol: number;
  gol_subiti: number;
  rigori_parati: number;
  porte_inviolate: number;
}

interface GiocatoreDetail {
  id: string;
  nome: string;
  club_serie_a: string | null;
  ruolo_classic: "P" | "D" | "C" | "A" | null;
  ruoli_mantra: string[] | null;
  stato: string;
  eta: number | null;
  data_nascita: string | null;
  transfermarkt_id: string | null;
  transfermarkt_url?: string | null;
  posseduto_da_squadra_id: string | null;
  valore_tm_milioni: string | null;
  valore_tm_data: string | null;
  storico_valori_tm: ValoreTM[];
  statistiche: StatisticheStagione | null;
}

// Andrea 2026-05-22: palette centralizzata.
const RUOLO_COLOR: Record<string, string> = {
  P: FULL_BY_GROUP.Por,
  D: FULL_BY_GROUP.Def,
  C: FULL_BY_GROUP.Mid,
  A: FULL_BY_GROUP.Att,
};

const RUOLO_LABEL: Record<string, string> = {
  P: tr("Portiere"),
  D: tr("Difensore"),
  C: tr("Centrocampista"),
  A: tr("Attaccante"),
};

const ULTIME_RILEVAZIONI = 5;

function Etichetta({ children }: { children: React.ReactNode }) {
  return (
    <div className="font-mono text-[10px] uppercase tracking-wider text-fg-muted">{children}</div>
  );
}

function dataBreve(iso: string): string {
  return new Date(iso).toLocaleDateString(localeCorrente(), {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
}

function media(v: string | null): string {
  return v === null ? "—" : Number(v).toFixed(2);
}

function Stat({
  valore,
  etichetta,
  tono = "neutro",
  grande = false,
}: {
  valore: string | number;
  etichetta: string;
  tono?: "neutro" | "oro" | "buono" | "cattivo";
  grande?: boolean;
}) {
  const colore = {
    neutro: "text-fg",
    oro: "text-accent",
    buono: "text-success",
    cattivo: "text-danger",
  }[tono];
  return (
    <div className="rounded-lg bg-primary-deep/40 border border-primary/15 px-2 py-2 text-center min-w-0">
      <div className={`font-display tabular-nums ${grande ? "text-2xl" : "text-xl"} ${colore}`}>
        {valore}
      </div>
      <div className="text-[10px] uppercase tracking-wider text-fg-muted leading-tight mt-0.5">
        {etichetta}
      </div>
    </div>
  );
}

export function GiocatoreDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: squadre } = useSquadre();

  const { data, isLoading, error } = useQuery({
    queryKey: ["giocatore", id],
    queryFn: async () => {
      const res = await api.get<GiocatoreDetail>(`/giocatori/${id}`);
      return res.data;
    },
    enabled: !!id,
  });

  const squadra =
    data && data.posseduto_da_squadra_id
      ? squadre?.find((s) => s.id === data.posseduto_da_squadra_id)
      : null;
  const portiere =
    data?.ruolo_classic === "P" ||
    (data?.ruoli_mantra ?? []).some((r) => r.trim().toLowerCase() === "por");
  const st = data?.statistiche ?? null;
  const ultime = data?.storico_valori_tm.slice(0, ULTIME_RILEVAZIONI) ?? [];

  return (
    <MobileShell logoSuffix="GIOCATORE">
      <div className="flex items-center justify-between gap-3">
        {/* Andrea 2026-10-15: si arriva anche da Gestione Rosa e Rose */}
        <button
          type="button"
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/giocatori"))}
          className="text-xs text-accent font-bold hover:underline"
        >
          {tr("← Indietro")}
        </button>
        <Link to="/giocatori" className="text-xs text-fg-muted hover:text-accent">
          {tr("Catalogo giocatori")}
        </Link>
      </div>

      {isLoading && (
        <div className="bg-bg-card border border-primary/20 rounded-card p-6 text-center text-fg-muted">
          {tr("Caricamento…")}
        </div>
      )}

      {error && (
        <div className="bg-danger/10 border border-danger/40 text-danger rounded-card p-3 text-sm">
          {tr("Giocatore non trovato.")}
        </div>
      )}

      {data && (
        <>
          {/* Blocco in alto: anagrafica · possesso + TM · ultime rilevazioni */}
          <div className="bg-gradient-to-br from-primary to-primary-deep rounded-card p-4 md:p-5 relative overflow-hidden shadow-hero">
            <div
              aria-hidden
              className="absolute -top-10 -right-10 w-40 h-40 rounded-full"
              style={{
                background: "radial-gradient(circle, rgba(212,175,55,0.18) 0%, transparent 70%)",
              }}
            />
            <div className="relative grid gap-4 md:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1fr)] md:gap-6">
              {/* Anagrafica */}
              <div className="flex items-start gap-3 min-w-0">
                <PlayerAvatar
                  nome={data.nome}
                  size={72}
                  className="shrink-0 ring-2 ring-accent/40"
                />
                <div className="flex-1 min-w-0">
                  <div className="font-display text-2xl md:text-3xl text-fg tracking-wide truncate">
                    {data.nome}
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                    {data.ruolo_classic && (
                      <span
                        className={`font-mono font-bold px-2 py-0.5 rounded text-xs ${RUOLO_COLOR[data.ruolo_classic]}`}
                      >
                        {RUOLO_LABEL[data.ruolo_classic]}
                      </span>
                    )}
                    {data.ruoli_mantra?.map((r) => (
                      <span
                        key={r}
                        className={`font-mono font-bold text-xs px-2 py-0.5 rounded ${ruoloMantraClass(r)}`}
                      >
                        {siglaRuolo(r)}
                      </span>
                    ))}
                  </div>
                  <div className="text-xs text-fg-muted mt-2 space-y-0.5">
                    {data.club_serie_a && (
                      <div>
                        {tr("Club:")} <span className="text-fg font-bold">{data.club_serie_a}</span>
                      </div>
                    )}
                    {data.eta != null && (
                      <div>
                        {tr("Età:")}{" "}
                        <span className="text-fg font-bold">
                          {tr("{{v1}} anni", { v1: data.eta })}
                        </span>
                        {data.data_nascita && (
                          <span>
                            {" "}
                            ·{" "}
                            {tr("nato il {{v1}}", {
                              v1: new Date(data.data_nascita).toLocaleDateString(localeCorrente()),
                            })}
                          </span>
                        )}
                      </div>
                    )}
                    <div>
                      {tr("Stato:")}{" "}
                      <span className="text-fg">
                        {data.stato === "serie_a" ? tr("Serie A") : tr("Fuori Serie A *")}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Possesso + valore TM */}
              <div className="grid grid-cols-2 gap-3 md:grid-cols-1 md:gap-3 md:border-l md:border-accent/20 md:pl-5">
                <div className="min-w-0">
                  <Etichetta>{tr("Possesso fantasy")}</Etichetta>
                  {squadra ? (
                    <div className="font-display text-lg md:text-xl text-success truncate">
                      {squadra.nome}
                    </div>
                  ) : (
                    <div className="font-display text-lg md:text-xl text-accent">
                      {tr("Svincolato")}
                    </div>
                  )}
                </div>
                <div className="min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <Etichetta>{tr("Valore Transfermarkt")}</Etichetta>
                    {data.transfermarkt_url && (
                      <a
                        href={data.transfermarkt_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[10px] text-accent font-bold whitespace-nowrap"
                      >
                        {tr("Profilo TM →")}
                      </a>
                    )}
                  </div>
                  {data.valore_tm_milioni ? (
                    <>
                      <div className="font-display text-3xl text-accent leading-tight">
                        {Number(data.valore_tm_milioni).toFixed(1)}
                        <span className="text-sm text-fg-muted font-sans ml-1">M€</span>
                      </div>
                      {data.valore_tm_data && (
                        <div className="text-[10px] text-fg-muted">
                          {tr("Ultima rilevazione: {{v1}}", {
                            v1: new Date(data.valore_tm_data).toLocaleDateString(localeCorrente()),
                          })}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-fg-muted text-xs">
                      {tr("Nessun valore rilevato.")}{" "}
                      <Link to="/admin/tm-import" className="text-accent underline">
                        {tr("Mod TM")}
                      </Link>
                    </div>
                  )}
                </div>
              </div>

              {/* Ultime rilevazioni TM */}
              <div className="md:border-l md:border-accent/20 md:pl-5 min-w-0">
                <Etichetta>{tr("Ultime rilevazioni TM")}</Etichetta>
                {ultime.length === 0 ? (
                  <div className="text-xs text-fg-muted mt-1">{tr("Nessuna rilevazione.")}</div>
                ) : (
                  <ul className="mt-1 divide-y divide-accent/10">
                    {ultime.map((v, i) => (
                      <li key={i} className="flex items-center justify-between gap-3 py-1 text-xs">
                        <span
                          className={`font-mono font-bold tabular-nums ${i === 0 ? "text-accent" : "text-fg"}`}
                        >
                          {Number(v.valore_milioni).toFixed(1)}M€
                        </span>
                        <span className="text-fg-muted tabular-nums">
                          {dataBreve(v.data_rilevazione)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>

          {/* Statistiche della stagione in corso */}
          <div className="bg-bg-card border border-primary/20 rounded-card overflow-hidden">
            <div className="px-4 py-2 bg-primary-deep/40 flex items-center justify-between gap-2">
              <div className="font-display text-sm text-accent tracking-wider uppercase">
                {tr("Statistiche")}
              </div>
              {st && (
                <div className="text-[10px] uppercase tracking-wider text-fg-muted">
                  {tr("Stagione {{v1}}", { v1: st.stagione_anno })}
                </div>
              )}
            </div>
            {!st ? (
              <div className="p-5 text-center text-sm text-fg-muted">
                {tr("Nessuna stagione in corso in questa lega.")}
              </div>
            ) : st.presenze === 0 ? (
              <div className="p-5 text-center text-sm text-fg-muted">
                {tr("Ancora nessun voto in questa stagione.")}
                <div className="text-xs mt-1">
                  {tr("I numeri compaiono con i voti finali delle giornate.")}
                </div>
              </div>
            ) : (
              <div className="p-3 space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  <Stat valore={st.presenze} etichetta={tr("Presenze")} grande />
                  <Stat valore={media(st.voto_medio)} etichetta={tr("Voto medio")} grande />
                  <Stat
                    valore={media(st.fantavoto_medio)}
                    etichetta={tr("Fantavoto medio")}
                    tono="oro"
                    grande
                  />
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                  <Stat
                    valore={st.gol}
                    etichetta={tr("Gol")}
                    tono={st.gol > 0 ? "buono" : "neutro"}
                  />
                  <Stat
                    valore={st.assist}
                    etichetta={tr("Assist")}
                    tono={st.assist > 0 ? "buono" : "neutro"}
                  />
                  <Stat
                    valore={st.ammonizioni}
                    etichetta={tr("Ammonizioni")}
                    tono={st.ammonizioni > 0 ? "cattivo" : "neutro"}
                  />
                  <Stat
                    valore={st.espulsioni}
                    etichetta={tr("Espulsioni")}
                    tono={st.espulsioni > 0 ? "cattivo" : "neutro"}
                  />
                  <Stat
                    valore={st.rigori_sbagliati}
                    etichetta={tr("Rigori sbagliati")}
                    tono={st.rigori_sbagliati > 0 ? "cattivo" : "neutro"}
                  />
                  <Stat
                    valore={st.autogol}
                    etichetta={tr("Autogol")}
                    tono={st.autogol > 0 ? "cattivo" : "neutro"}
                  />
                </div>
                {portiere && (
                  <div className="grid grid-cols-3 gap-2">
                    <Stat
                      valore={st.gol_subiti}
                      etichetta={tr("Gol subiti")}
                      tono={st.gol_subiti > 0 ? "cattivo" : "neutro"}
                    />
                    <Stat
                      valore={st.rigori_parati}
                      etichetta={tr("Rigori parati")}
                      tono={st.rigori_parati > 0 ? "buono" : "neutro"}
                    />
                    <Stat
                      valore={st.porte_inviolate}
                      etichetta={tr("Porte inviolate")}
                      tono={st.porte_inviolate > 0 ? "buono" : "neutro"}
                    />
                  </div>
                )}
                <div className="text-[10px] text-fg-muted">
                  {tr(
                    "Dai voti finali delle giornate della lega: una presenza è una giornata con voto; i 6 d'ufficio per rinvio non contano.",
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </MobileShell>
  );
}
